import {
  Button,
  Combobox,
  type ComboboxOption,
  Drawer,
  Select,
  type SelectOption,
} from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { fetchAllCompanies } from "./components";

type YardFormData = {
  moaCompanyId: number | null;
  name: string;
  type: "lots_separes" | "entreprise_generale" | "conception_realisation";
  localisationData?: string;
  comissioningDate?: string;
};

type Yard = {
  id: number;
  moaCompanyId: number | null;
  name: string;
  type: "lots_separes" | "entreprise_generale" | "conception_realisation";
  localisationData: string | null;
  comissioningDate: string | null;
};

const yardTypeOptions: SelectOption[] = [
  { label: "Lots séparés", value: "lots_separes" },
  { label: "Entreprise générale", value: "entreprise_generale" },
  { label: "Conception-réalisation", value: "conception_realisation" },
];

export type YardFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  yard?: Yard;
  onSuccess?: () => void;
};

export function YardForm({
  open,
  onOpenChange,
  yard,
  onSuccess,
}: YardFormProps) {
  const [companyOptions, setCompanyOptions] = useState<ComboboxOption[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  const isEdit = !!yard;

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<YardFormData>({
    defaultValues: {
      moaCompanyId: yard?.moaCompanyId || null,
      name: yard?.name || "",
      type: yard?.type || "lots_separes",
      localisationData: yard?.localisationData || undefined,
      comissioningDate: yard?.comissioningDate || undefined,
    },
  });

  const selectedType = watch("type");
  const selectedMoaCompanyId = watch("moaCompanyId");

  // Load companies
  useEffect(() => {
    async function fetchCompanies() {
      setIsLoadingCompanies(true);
      const { companies } = await fetchAllCompanies();
      if (isNil(companies)) {
        toaster.error({
          title: "Erreur",
          description: "Impossible de charger les entreprises",
        });
        setIsLoadingCompanies(false);
        return;
      }

      const options: ComboboxOption[] = companies.map((company) => ({
        label: company.name,
        value: String(company.id),
      }));
      setCompanyOptions(options);
      setIsLoadingCompanies(false);
    }
    if (open) {
      fetchCompanies();
    }
  }, [open]);

  // Reset form when yard changes or drawer closes
  useEffect(() => {
    if (open) {
      reset({
        moaCompanyId: yard?.moaCompanyId || null,
        name: yard?.name || "",
        type: yard?.type || "lots_separes",
        localisationData: yard?.localisationData || undefined,
        comissioningDate: yard?.comissioningDate || undefined,
      });
    }
  }, [open, yard, reset]);

  const onSubmit = async (data: YardFormData) => {
    try {
      if (isEdit) {
        const { error } = await apiClient.PATCH("/api/admin/yards/{id}", {
          params: { path: { id: yard.id } },
          body: data,
        });

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la modification du chantier",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Chantier modifié avec succès",
        });
      } else {
        const { error } = await apiClient.POST("/api/admin/yards", {
          body: data,
        });

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la création du chantier",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Chantier créé avec succès",
        });
      }

      onSuccess?.();
      onOpenChange(false);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    }
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>
                  {isEdit ? "Modifier le chantier" : "Créer un chantier"}
                </Drawer.Title>
                <Drawer.Description>
                  {isEdit
                    ? "Modifiez les informations du chantier"
                    : "Créez un nouveau chantier"}
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                {/* MOA Company Combobox */}
                <div>
                  <label
                    htmlFor="moaCompanyId"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Entreprise MOA
                  </label>
                  <p className="mb-2 text-xs text-zinc-500">
                    Maitre d'Ouvrage - l'entreprise responsable du chantier
                  </p>
                  <Combobox
                    options={companyOptions}
                    value={
                      selectedMoaCompanyId ? [String(selectedMoaCompanyId)] : []
                    }
                    onValueChange={(details) => {
                      const value = details.value[0];
                      setValue("moaCompanyId", value ? Number(value) : null);
                    }}
                    placeholder="Rechercher une entreprise"
                    disabled={isLoadingCompanies}
                  />
                </div>

                {/* Name Input */}
                <div>
                  <label
                    htmlFor="name"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    {...register("name")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Nom du chantier"
                  />
                </div>

                {/* Type Select */}
                <div>
                  <label
                    htmlFor="type"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={yardTypeOptions}
                    value={[selectedType]}
                    onValueChange={(details) => {
                      const value = details.value[0];
                      if (value) {
                        setValue(
                          "type",
                          value as
                            | "lots_separes"
                            | "entreprise_generale"
                            | "conception_realisation",
                        );
                      }
                    }}
                    placeholder="Sélectionner un type"
                  />
                </div>

                {/* Localisation Input */}
                <div>
                  <label
                    htmlFor="localisationData"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Localisation
                  </label>
                  <input
                    id="localisationData"
                    type="text"
                    {...register("localisationData")}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Adresse ou localisation"
                  />
                </div>

                {/* Date Commissioning Input */}
                <div>
                  <label
                    htmlFor="comissioningDate"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Date de mise en service
                  </label>
                  <input
                    id="comissioningDate"
                    type="date"
                    {...register("comissioningDate")}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                  />
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex shrink-0 justify-end gap-3 border-t border-zinc-200 p-6 dark:border-zinc-800">
                <Button
                  type="button"
                  outline
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "En cours..." : isEdit ? "Modifier" : "Créer"}
                </Button>
              </div>
            </form>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
